import { createRouter } from "@tanstack/react-router"

import { accessStore } from "../access/access-store"
import { AppLoading } from "../shell/states"
import {
  chooseTenantRoute,
  createSalonRoute,
  enrollMfaRoute,
  forgotPasswordRoute,
  inviteRoute,
  loginRoute,
  mfaRoute,
  resetPasswordRoute,
} from "./routes/auth-routes"
import { masterRoute, masterRoutes } from "./routes/master-routes"
import { protectedRoute } from "./routes/protected-route"
import { rootRoute } from "./routes/root-route"
import { sectionRoutes } from "./routes/section-routes"
import { uiShowcaseRoute } from "./routes/ui-route"

/**
 * Маршрутизатор кабинета.
 *
 * ПОЧЕМУ TANSTACK ROUTER И ПОЧЕМУ БЕЗ ФАЙЛОВОЙ ГЕНЕРАЦИИ.
 *
 * 1. Типы без генерации. Адрес, параметры адреса и параметры строки запроса проверяет
 *    компилятор: опечатка в `to="/jurnal"` или забытый параметр дня не доживают до сборки.
 *    Это прямо требование ТЗ §4 — состояние экранов живёт в адресе, а значит адрес
 *    становится частью контракта интерфейса и обязан проверяться как контракт.
 *
 * 2. beforeLoad с контекстом — единственный способ выполнить правило «проверка прав ждёт
 *    загрузки профиля» честно: ожидание происходит ДО отрисовки, а не в первом кадре.
 *
 * 3. Маршруты описаны кодом, а не файлами, потому что после этого каркаса над кабинетом
 *    работают три агента одновременно. Файловая генерация свела бы их правки в один
 *    routeTree.gen.ts, который переписывается целиком при каждом добавлении экрана;
 *    здесь же каждый добавляет свой createRoute и одну строку в дерево.
 */

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  enrollMfaRoute,
  mfaRoute,
  chooseTenantRoute,
  createSalonRoute,
  inviteRoute,
  uiShowcaseRoute,
  // Экраны мастера стоят РЯДОМ с кабинетом, а не внутри него: у мастера своя оболочка
  // с четырьмя вкладками телефона, и общая навигация разделов ему не нужна ни одним
  // пунктом (см. app/routes/master-routes.tsx).
  masterRoute.addChildren(masterRoutes),
  protectedRoute.addChildren(sectionRoutes),
])

export const router = createRouter({
  routeTree,
  context: { access: accessStore },
  // Ноль намеренно: каркас загрузки показывается СРАЗУ. По умолчанию маршрутизатор
  // ждёт секунду, надеясь, что загрузка окажется мгновенной, — и в эту секунду
  // человек смотрит на прошлый экран, который уже неверен.
  defaultPendingMs: 0,
  defaultPendingMinMs: 0,
  defaultPendingComponent: AppLoading,
  // Кабинет открывают по прямой ссылке из чата: неизвестный адрес обязан дать
  // понятную страницу, а не белый экран.
  defaultPreload: false,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}
